/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Search, Sparkles, Camera } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { toast } from "sonner";
import VinScannerDialog from "@/components/VinScannerDialog";
import AiDescriptionTools from "@/components/AiDescriptionTools";
import PhotoUploader from "@/components/PhotoUploader";

const BLANK = {
  vin: "",
  year: new Date().getFullYear(),
  make: "",
  model: "",
  price: "",
  mileage: "",
  body_type: "",
  transmission: "",
  fuel_type: "",
  drivetrain: "",
  exterior_color: "",
  interior_color: "",
  description: "",
  image_urls: [],
  featured: false,
};

export default function QuickAddVehicleDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [decoding, setDecoding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);

  const reset = () => setForm(BLANK);

  const decodeVin = async (vinOverride) => {
    const v = ((typeof vinOverride === "string" ? vinOverride : form.vin) || "").trim().toUpperCase();
    if (v.length !== 17) {
      toast.error("VIN must be 17 characters");
      return;
    }
    setDecoding(true);
    try {
      const r = await api.get(`/vin/decode/${v}`);
      const d = r.data || {};
      setForm((f) => ({
        ...f,
        year: d.year || f.year,
        make: (d.make || f.make || "").toString().replace(/\b\w/g, (c) => c.toUpperCase()),
        model: d.model || f.model,
        body_type: d.body_type || f.body_type,
        transmission: d.transmission || f.transmission,
        fuel_type: d.fuel_type || f.fuel_type,
        drivetrain: d.drivetrain || f.drivetrain,
      }));
      toast.success("VIN decoded — review the auto-filled fields");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "VIN lookup failed");
    } finally {
      setDecoding(false);
    }
  };

  const descFacts = () => {
    if (!form.year || !form.make || !form.model) {
      toast.error("Add year/make/model first");
      return null;
    }
    return {
      year: parseInt(form.year, 10),
      make: form.make,
      model: form.model,
      mileage: form.mileage ? parseInt(form.mileage, 10) : null,
      body_type: form.body_type || null,
      exterior_color: form.exterior_color || null,
      interior_color: form.interior_color || null,
      transmission: form.transmission || null,
      fuel_type: form.fuel_type || null,
      drivetrain: form.drivetrain || null,
      price: form.price ? parseFloat(form.price) : null,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.make || !form.model || !form.year || !form.price) {
      toast.error("Year, Make, Model, and Price are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/cars", {
        vin: form.vin || null,
        year: parseInt(form.year, 10),
        make: form.make.trim(),
        model: form.model.trim(),
        price: parseFloat(form.price),
        mileage: form.mileage ? parseInt(form.mileage, 10) : 0,
        body_type: form.body_type || "Sedan",
        transmission: form.transmission || "Automatic",
        fuel_type: form.fuel_type || "Gasoline",
        drivetrain: form.drivetrain || "FWD",
        exterior_color: form.exterior_color || "",
        description: form.description || "",
        image_urls: form.image_urls,
        featured: form.featured,
        status: "available",
      });
      toast.success("Vehicle added to inventory");
      reset();
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quick-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-primary">Quick Add Vehicle</DialogTitle>
          <DialogDescription>
            Paste a VIN to auto-fill, snap photos from your phone, and let AI draft the description.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* VIN ROW */}
          <div className="border border-soft bg-secondary/40 p-4 rounded-sm">
            <Label htmlFor="qa-vin" className="font-medium text-primary">VIN (auto-fill specs)</Label>
            <div className="mt-2 flex gap-2">
              <Input
                id="qa-vin"
                placeholder="17-character VIN — leave blank if you don't have it"
                value={form.vin}
                onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })}
                className="font-mono text-sm"
                maxLength={17}
                data-testid="qa-vin-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setScanOpen(true)}
                className="rounded-sm gap-1"
                title="Scan VIN with camera"
                data-testid="qa-scan-vin"
              >
                <Camera className="h-3 w-3" /> Scan
              </Button>
              <Button
                type="button"
                onClick={decodeVin}
                disabled={decoding || (form.vin || "").length !== 17}
                className="rounded-sm gap-1 bg-accent text-accent-foreground hover:bg-accent/90"
                data-testid="qa-decode-vin"
              >
                <Search className="h-3 w-3" /> {decoding ? "Decoding…" : "Decode"}
              </Button>
            </div>
          </div>

          {/* CORE FIELDS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label htmlFor="qa-year">Year *</Label>
              <Input id="qa-year" type="number" required value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="qa-year" />
            </div>
            <div>
              <Label htmlFor="qa-make">Make *</Label>
              <Input id="qa-make" required value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} data-testid="qa-make" />
            </div>
            <div>
              <Label htmlFor="qa-model">Model *</Label>
              <Input id="qa-model" required value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="qa-model" />
            </div>
            <div>
              <Label htmlFor="qa-price">Price ($) *</Label>
              <Input id="qa-price" type="number" required placeholder="18995" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="qa-price" />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label htmlFor="qa-mileage">Mileage</Label>
              <Input id="qa-mileage" type="number" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} data-testid="qa-mileage" />
            </div>
            <div>
              <Label htmlFor="qa-color">Exterior Color</Label>
              <Input id="qa-color" value={form.exterior_color} onChange={(e) => setForm({ ...form, exterior_color: e.target.value })} data-testid="qa-color" />
            </div>
            <div>
              <Label htmlFor="qa-int-color">Interior Color</Label>
              <Input id="qa-int-color" value={form.interior_color} onChange={(e) => setForm({ ...form, interior_color: e.target.value })} placeholder="Black / Gray / Tan" data-testid="qa-interior-color" />
            </div>
            <div>
              <Label htmlFor="qa-body">Body Type</Label>
              <Input id="qa-body" value={form.body_type} onChange={(e) => setForm({ ...form, body_type: e.target.value })} placeholder="Sedan / SUV / Truck" data-testid="qa-body" />
            </div>
            <div>
              <Label htmlFor="qa-trans">Transmission</Label>
              <Input id="qa-trans" value={form.transmission} onChange={(e) => setForm({ ...form, transmission: e.target.value })} placeholder="Automatic" />
            </div>
          </div>

          {/* PHOTO UPLOAD */}
          <PhotoUploader
            urls={form.image_urls}
            onChange={(updater) => setForm((f) => ({ ...f, image_urls: updater(f.image_urls) }))}
            testIdPrefix="qa-photo"
          />

          {/* DESCRIPTION + AI */}
          <div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Label htmlFor="qa-desc">Description</Label>
              <AiDescriptionTools
                getFacts={descFacts}
                onPick={(text) => setForm((f) => ({ ...f, description: text }))}
                disabled={!form.make || !form.model}
                testIdPrefix="qa-ai"
              />
            </div>
            <Textarea
              id="qa-desc"
              rows={10}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Tell customers what's great about this car — or click 'Generate with AI'."
              className="mt-2"
              data-testid="qa-description"
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="qa-featured"
              checked={form.featured}
              onCheckedChange={(c) => setForm({ ...form, featured: !!c })}
              data-testid="qa-featured"
            />
            <Label htmlFor="qa-featured" className="cursor-pointer">Feature on homepage</Label>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm" data-testid="qa-submit">
              {saving ? "Saving…" : "Add to Inventory"}
            </Button>
          </DialogFooter>
        </form>
        <VinScannerDialog
          open={scanOpen}
          onOpenChange={setScanOpen}
          onDetected={(vin) => {
            setForm((f) => ({ ...f, vin }));
            decodeVin(vin);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
